import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { featureApi, MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

export interface MemeConfig {
  characterImageUrl: string | null;
  characterDescription: string;
}

const MOCK_CONFIG: MemeConfig = {
  characterImageUrl: null,
  characterDescription: '',
};

export const memeConfigKeys = {
  all: ['memeConfig'] as const,
  config: (projectId: string) => [...memeConfigKeys.all, projectId] as const,
};

export function useMemeConfig(): ReturnType<typeof useQuery<MemeConfig>> {
  const { projectId } = useCurrentProject();

  return useQuery({
    queryKey: memeConfigKeys.config(projectId ?? ''),
    queryFn: async () => {
      if (MOCK_MODE) return MOCK_CONFIG;
      return featureApi.get<MemeConfig>(`/meme/config?projectId=${projectId}`);
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateMemeConfig(): ReturnType<typeof useMutation<MemeConfig, Error, Partial<MemeConfig>>> {
  const queryClient = useQueryClient();
  const { projectId } = useCurrentProject();

  return useMutation({
    mutationFn: async (data: Partial<MemeConfig>) => {
      if (MOCK_MODE) {
        return { ...MOCK_CONFIG, ...data } satisfies MemeConfig;
      }
      return featureApi.patch<MemeConfig>(`/meme/config`, { ...data, projectId });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(memeConfigKeys.config(projectId ?? ''), updated);
    },
  });
}

export function useUploadCharacterImage(): ReturnType<typeof useMutation<{ url: string }, Error, File>> {
  return useMutation({
    mutationFn: async (file: File) => {
      if (MOCK_MODE) {
        return { url: URL.createObjectURL(file) };
      }
      return featureApi.uploadImage('/meme/upload', file);
    },
  });
}
